import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SAGA_ACTIONS } from "src/_config";
import { useCandidatesApi } from "src/_common/hooks/actions/candidates/appCandidatesApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import ViewProfileModal from "../job-details/_common/ViewProfileModal";
import CandidateAdvancedFilters from "./CandidateAdvancedFilters";
import { ReactSearchAutocomplete } from "react-search-autocomplete";

interface FilterState {
  exp_min: string;
  exp_max: string;
  industry_ids: number[];
  country_id: string;
  city_id: string;
  education_ids: number[];
  language_ids: number[];
  nationality_ids: number[];
  gender_id: string;
  cv_completeness: string;
  last_updated: string;
  last_visited: string;
  has_video_cv: boolean;
}

interface Jobseeker {
  jobseeker: { id: number; email?: string; mobile_number?: string; last_login_date?: string };
  basic_details: {
    full_name: string;
    first_name?: string;
    last_name?: string;
    cv_headline: string;
    profile_score?: number;
    total_experience_year?: string;
    total_experience_month?: string;
    gender_id?: number;
    nationality_id?: number;
    birth_date?: string;
    video_file?: string;
    video_status?: number;
    last_updated_at?: string;
    updated_at?: string;
    system_resume_last_generated_at?: string;
  };
  document_full_path: {
    profile_pic: string | null;
    resume_file: string | null;
    video_file: string | null;
    system_resume_file: string | null;
  };
  country_details?: { id: number; name: string };
  employment_details?: any[];
  education_details?: any[];
  certification_details?: any[];
  course_details?: any[];
  project_details?: any[];
  jobseeker_public_profiles?: any[];
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number | null;
  total_pages: number | null;
  has_more: boolean;
  returned_count: number;
}

// Compute date string for CV activity filters
const getDateRange = (value: string): { from?: string; to?: string } => {
  if (!value) return {};
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  if (value === "1month") {
    const from = new Date(now);
    from.setDate(from.getDate() - 30);
    return { from: fmt(from) };
  }
  if (value === "3months") {
    const from = new Date(now);
    from.setDate(from.getDate() - 90);
    return { from: fmt(from) };
  }
  if (value === "older") {
    const to = new Date(now);
    to.setDate(to.getDate() - 90);
    return { to: fmt(to) };
  }
  return {};
};

const formatTimeAgo = (dateStr: string | undefined | null): string => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "-";
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return "1 month ago";
  if (diffMonths < 12) return `${diffMonths} months ago`;
  return "more than a year ago";
};

const calcAge = (birthDate: string | undefined): string => {
  if (!birthDate) return "";
  const dob = new Date(birthDate);
  if (isNaN(dob.getTime())) return "";
  const age = Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  return age > 0 ? String(age) : "";
};

const CandidateAdvancedMain: React.FC = () => {
  const dispatch = useDispatch();
  const toast = useToaster();
  const { callGetJobseekersAdvanced } = useCandidatesApi();

  // Get job title suggestions from Redux
  const jobTitleSuggestionsFromRedux = useSelector((state: any) => state.global.jobTitleSuggestions || []);

  // Job title search
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [jobTitleSelected, setJobTitleSelected] = useState("");
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState<Array<{ id: string; name: string }>>([]);
  const [jobTitleError, setJobTitleError] = useState(false);
  const [jobTitleKey, setJobTitleKey] = useState(0);
  const jobTitleDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const jobTitleCurrentSearchRef = useRef<string>("");

  // Sync Redux data to local state
  useEffect(() => {
    if (jobTitleSuggestionsFromRedux && jobTitleSuggestionsFromRedux.length > 0) {
      const formatted = jobTitleSuggestionsFromRedux.map((item: any) => ({
        id: item.name,
        name: item.name,
      }));
      setJobTitleSuggestions(formatted);
    }
  }, [jobTitleSuggestionsFromRedux]);

  // Results
  const [jobseekers, setJobseekers] = useState<Jobseeker[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState<FilterState | null>(null);
  const [currentFilters, setCurrentFilters] = useState<FilterState | null>(null);

  // Profile modal
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [selectedJobseeker, setSelectedJobseeker] = useState<any>(null);

  const itemsPerPage = 12;

  // Fetch job title suggestions API call
  const fetchJobTitleSuggestions = (query: string) => {
    jobTitleCurrentSearchRef.current = query;
    dispatch({
      type: SAGA_ACTIONS.COMMON.JOB_TITLE_SUGGESTIONS,
      payload: { q: query, limit: 30, offset: 0 },
    });
  };

  // Handle job title search with debounce
  const handleJobTitleSearch = (query: string) => {
    setJobTitleInput(query);
    setJobTitleSelected(""); // clear selected when typing
    
    // Clear previous debounce timer
    if (jobTitleDebounceRef.current) {
      clearTimeout(jobTitleDebounceRef.current);
    }
    
    if (query.length < 2) {
      setJobTitleSuggestions([]);
      return;
    }
    
    // Debounce API call by 300ms
    jobTitleDebounceRef.current = setTimeout(() => {
      fetchJobTitleSuggestions(query);
    }, 300);
  };

  const handleJobTitleSelect = (item: any) => {
    setJobTitleSelected(item.name);
    setJobTitleInput(item.name);
    setJobTitleError(false);
  };

  const buildParams = (filters: FilterState | null, page: number, jobTitle: string) => {
    const params: any = {
      job_title: jobTitle,
      page,
      per_page: itemsPerPage,
    };

    if (!filters) return params;

    if (filters.exp_min) params.exp_min = filters.exp_min;
    if (filters.exp_max) params.exp_max = filters.exp_max;
    if (filters.industry_ids.length > 0) params.industry_ids = filters.industry_ids;
    if (filters.country_id) params.country_id = filters.country_id;
    if (filters.city_id) params.city_id = filters.city_id;
    if (filters.education_ids.length > 0) params.education_ids = filters.education_ids;
    if (filters.language_ids.length > 0) params.language_ids = filters.language_ids;
    if (filters.nationality_ids.length > 0) params.nationality_ids = filters.nationality_ids;
    if (filters.gender_id) params.gender_id = filters.gender_id;
    if (filters.cv_completeness) params.cv_completeness = filters.cv_completeness;
    if (filters.has_video_cv) params.has_video_cv = 1;

    // CV Activity - Last Updated
    if (filters.last_updated) {
      const range = getDateRange(filters.last_updated);
      if (range.from) params.last_active_from = range.from;
      if (range.to) params.last_active_to = range.to;
    }

    // CV Activity - Last Visited
    if (filters.last_visited) {
      const range = getDateRange(filters.last_visited);
      if (range.from) params.last_visited_from = range.from;
      if (range.to) params.last_visited_to = range.to;
    }

    return params;
  };

  const fetchJobseekers = (isLoadMore: boolean, page: number, filters: FilterState | null, jobTitle: string) => {
    const params = buildParams(filters, page, jobTitle);
    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    callGetJobseekersAdvanced(
      params,
      (_msg: string, data: any) => {
        if (isLoadMore) {
          setLoadingMore(false);
          setJobseekers((prev) => [...prev, ...(data?.jobseekers || [])]);
        } else {
          setLoading(false);
          setJobseekers(data?.jobseekers || []);
        }
        setPagination(data?.pagination || null);
      },
      (error: any) => {
        if (isLoadMore) setLoadingMore(false);
        else {
          setLoading(false);
          setJobseekers([]);
          setPagination(null);
        }
        const msg = error?.message || error?.data?.message || "Something went wrong";
        toast.error(msg);
      },
    );
  };

  const handleSearch = () => {
    const title = jobTitleSelected || jobTitleInput;
    if (!title.trim()) {
      setJobTitleError(true);
      toast.error("Please select a job title");
      return;
    }
    setJobTitleError(false);
    setCurrentPage(1);
    setHasSearched(true);
    // Use currentFilters (filters being edited) instead of appliedFilters
    const filtersToUse = currentFilters || appliedFilters;
    setAppliedFilters(filtersToUse);
    fetchJobseekers(false, 1, filtersToUse, title.trim());
  };

  const handleFilterChange = (filters: FilterState) => {
    setCurrentFilters(filters);
  };

  const handleApplyFilters = (filters: FilterState) => {
    const title = jobTitleSelected || jobTitleInput;
    if (!title.trim()) {
      toast.error("Please select a job title");
      return;
    }
    setAppliedFilters(filters);
    if (hasSearched) {
      setCurrentPage(1);
      fetchJobseekers(false, 1, filters, title.trim());
    }
  };

  const handleResetFilters = () => {
    setAppliedFilters(null);
    setCurrentFilters(null);
    const title = jobTitleSelected || jobTitleInput;
    if (hasSearched && title.trim()) {
      setCurrentPage(1);
      fetchJobseekers(false, 1, null, title.trim());
    }
  };

  const handleReset = () => {
    setJobTitleInput("");
    setJobTitleSelected("");
    setJobTitleKey((k) => k + 1);
    setAppliedFilters(null);
    setCurrentFilters(null);
    setHasSearched(false);
    setJobseekers([]);
    setPagination(null);
    setCurrentPage(1);
    setJobTitleError(false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchJobseekers(true, nextPage, appliedFilters, jobTitleSelected || jobTitleInput);
  };

  const handleViewProfile = (jobseeker: Jobseeker) => {
    const basic = jobseeker.basic_details || {};
    const account = jobseeker.jobseeker || {};
    const skills = (basic as any).key_skills_custome || (basic as any).key_skills || [];

    const formatted = {
      id: account.id,
      jobseekerId: account.id,
      name: basic.full_name,
      avatar: jobseeker.document_full_path?.profile_pic,
      position: basic.cv_headline || jobseeker.employment_details?.[0]?.designation || "",
      resume_file: jobseeker.document_full_path?.system_resume_file,
      video_file: jobseeker.document_full_path?.video_file,
      currentDesignation: basic.cv_headline || jobseeker.employment_details?.[0]?.designation || "",
      currentLocation: (basic as any).current_location || "",
      gender: basic.gender_id === 1 ? "Male" : basic.gender_id === 2 ? "Female" : "",
      totalExperienceYear: basic.total_experience_year || null,
      currentSalary: (basic as any).current_salary || null,
      currentSalaryCurrency: (basic as any).current_salary_currency || null,
      skills: Array.isArray(skills) ? skills.join(", ") : skills,
      profileSummary: (basic as any).profile_summary || "",
      dateOfBirth: basic.birth_date || "",
      languages: (basic as any).known_languages || "-",
      basicDetails: {
        jobseeker_id: account.id,
        name: basic.full_name,
        first_name: basic.first_name || "",
        last_name: basic.last_name || "",
        email: account.email || "",
        mobile_number: account.mobile_number || "",
        current_designation: basic.cv_headline || "",
        current_location: (basic as any).current_location || "",
        key_skills: skills,
        profile_summary: (basic as any).profile_summary || "",
        resume_file: jobseeker.document_full_path?.system_resume_file,
        video_file: jobseeker.document_full_path?.video_file,
        preferred_country: (basic as any).preferred_country || [],
        gender_id: basic.gender_id,
        known_languages: (basic as any).known_languages || "",
      },
      jobseekerAccount: {
        secondary_email: (account as any).secondary_email || "",
        secondary_mobile_number: (account as any).secondary_mobile_number || "",
        secondary_country_code: (account as any).secondary_country_code || "",
      },
      employmentDetails: jobseeker.employment_details || [],
      educationDetails: jobseeker.education_details || [],
      courseDetails: jobseeker.course_details || [],
      projectDetails: jobseeker.project_details || [],
      certificateDetails: jobseeker.certification_details || [],
      document_full_path: jobseeker.document_full_path || {},
      jobseeker_public_profiles: jobseeker.jobseeker_public_profiles || [],
    };

    setSelectedJobseeker(formatted);
    setIsViewProfileOpen(true);
  };

  const getExperienceLabel = (js: Jobseeker) => {
    // Use calculated total_experience from API (calculated from employment details)
    const totalExp = (js as any).total_experience;
    if (totalExp !== undefined && totalExp !== null && totalExp > 0) {
      const years = Math.floor(totalExp);
      const months = Math.round((totalExp - years) * 12);
      const parts: string[] = [];
      if (years > 0) {
        parts.push(`${years} Year${years !== 1 ? "s" : ""}`);
      }
      if (months > 0) {
        parts.push(`${months} month${months !== 1 ? "s" : ""}`);
      }
      return parts.length > 0 ? parts.join(", ") : null;
    }
    
    // Fallback to basic_details fields if total_experience not available
    const y = js.basic_details?.total_experience_year;
    const m = js.basic_details?.total_experience_month;
    if (!y && !m) return null;
    const parts: string[] = [];
    
    // Parse year - handle both "6" and "6 Years" format
    if (y) {
      const yearNum = parseInt(y);
      if (yearNum > 0) {
        parts.push(`${yearNum} Year${yearNum !== 1 ? "s" : ""}`);
      }
    }
    
    // Parse month - only show if > 0
    if (m) {
      const monthNum = parseInt(m);
      if (monthNum > 0) {
        parts.push(`${monthNum} month${monthNum !== 1 ? "s" : ""}`);
      }
    }
    
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const getLastVisited = (js: Jobseeker) => {
    // Last visited = last_updated_at or updated_at from basic_details
    const d = js.basic_details?.last_updated_at || js.basic_details?.updated_at;
    return formatTimeAgo(d);
  };

  const getLastUpdated = (js: Jobseeker) => {
    // Last updated = system_resume_last_generated_at from basic_details
    const d = js.basic_details?.system_resume_last_generated_at;
    return formatTimeAgo(d);
  };

  const getEducationSummary = (js: Jobseeker) => {
    if (!js.education_details || js.education_details.length === 0) return null;
    const first = js.education_details[0];
    const parts: string[] = [];
    if (first.degree) parts.push(first.degree);
    if (first.specialization) parts.push(first.specialization);
    if (first.institute_name) parts.push(first.institute_name);
    const extra = js.education_details.length - 1;
    return { summary: parts.join(", "), extra };
  };

  return (
    <div className="dashboard-main member-page-wrapper">
      <div className="advanced-search-wrapper">
        {/* Top Search Bar */}
        <div className="advanced-search-topbar">
          <div className={`advanced-search-input-wrap ${jobTitleError ? "has-error" : ""}`}>
            <ReactSearchAutocomplete
              key={`job-title-${jobTitleKey}`}
              items={jobTitleSuggestions}
              onSearch={handleJobTitleSearch}
              onSelect={handleJobTitleSelect}
              onClear={() => {
                setJobTitleInput("");
                setJobTitleSelected("");
                setJobTitleSuggestions([]);
              }}
              placeholder="Search by job title..."
              inputSearchString={jobTitleInput}
              styling={{
                height: "46px",
                border: jobTitleError ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
                borderRadius: "8px",
                boxShadow: "none",
                zIndex: 9999,
                fontSize: "15px",
              }}
              showIcon={true}
              autoFocus={false}
              showNoResults={false}
              fuseOptions={{ threshold: 1 }}
              maxResults={50}
            />
            {jobTitleError && (
              <span className="advanced-search-error">Job title is required</span>
            )}
          </div>

          <button className="advanced-search-btn" onClick={handleSearch}>
            Search CVs
          </button>

          <button className="advanced-search-reset-btn" onClick={handleReset}>
            Reset search
          </button>
        </div>

        <div className="advanced-search-body">
          {/* Left Sidebar Filters */}
          <aside className="advanced-search-sidebar">
            <CandidateAdvancedFilters
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              onFilterChange={handleFilterChange}
            />
          </aside>

          {/* Main Results Area */}
          <main className="advanced-search-results">
            {!hasSearched ? (
              <div className="advanced-search-empty-state">
                <div className="advanced-search-empty-icon">
                  <i className="fa fa-search"></i>
                </div>
                <h2>Start your search with a Job Title</h2>
                {/* <p className="advanced-search-tips-title">Search tips:</p> */}
                <ol className="advanced-search-tips-list">
                  {/* <li>Search with relevant keywords for job, skills, companies, etc.</li> */}
                  <li>Use filters to narrow down your search results.</li>
                </ol>
              </div>
            ) : loading ? (
              <div className="advanced-search-loading">
                <i className="fa fa-spinner fa-spin"></i>
                <span>Loading candidates...</span>
              </div>
            ) : jobseekers.length > 0 ? (
              <>
                {/* Job Post Banner - Always show when results exist */}
                <div className="advanced-search-job-banner">
                  <div className="job-banner-icon">
                    <i className="fa fa-magic"></i>
                  </div>
                  <div className="job-banner-content">
                    <p className="job-banner-title">
                      Need a job post for "{jobTitleSelected || jobTitleInput}"?
                    </p>
                    <p className="job-banner-desc">
                      Try our <span className="ai-highlight">AI-powered</span> job posting feature to create it instantly!
                    </p>
                  </div>
                  <button 
                    className="job-banner-btn"
                    onClick={() => window.location.href = "/job-create"}
                  >
                    Post a job
                  </button>
                  <button className="job-banner-close" onClick={(e) => (e.currentTarget.parentElement as HTMLElement).style.display = 'none'}>
                    <i className="fa fa-times"></i>
                  </button>
                </div>

                <div className="advanced-search-results-header">
                  <span className="advanced-search-count">
                    {pagination?.total ? `${pagination.total.toLocaleString()}+ CVs matching your search` : `${jobseekers.length} results`}
                  </span>
                </div>

                <div className="advanced-candidates-list">
                  {jobseekers.map((js) => {
                    const basic = js.basic_details;
                    const eduInfo = getEducationSummary(js);
                    const exp = getExperienceLabel(js);
                    const age = calcAge(basic?.birth_date);
                    const lastVisited = getLastVisited(js);
                    const lastUpdated = getLastUpdated(js);

                    return (
                      <div key={js.jobseeker.id} className="advanced-candidate-card">
                        {/* Card top row: timestamps + actions */}
                        <div className="advanced-card-toprow">
                          <span className="advanced-card-activity">
                            Last visited: <strong>{lastVisited !== "-" ? lastVisited : "--"}</strong>
                            {" · "}
                            Last updated: <strong>{lastUpdated !== "-" ? lastUpdated : "--"}</strong>
                          </span>
                          <div className="advanced-card-actions">
                            <button
                              className="advanced-card-btn-primary"
                              onClick={() => handleViewProfile(js)}
                            >
                              View Profile
                            </button>
                            <button
                              className="advanced-card-btn-secondary"
                              onClick={() => {}}
                            >
                              <i className="fa fa-comment-alt"></i> Message
                            </button>
                          </div>
                        </div>

                        {/* Card body: avatar + info */}
                        <div className="advanced-card-body">
                          <div className="advanced-card-avatar">
                            <img
                              src={js.document_full_path?.profile_pic || "/images/placeholder.jpg"}
                              alt={basic?.full_name}
                              onError={(e) => {
                                e.currentTarget.src = "/images/placeholder.jpg";
                              }}
                            />
                          </div>

                          <div className="advanced-card-info">
                            <div className="advanced-card-name-row">
                              <h3 className="advanced-card-name">{basic?.full_name}</h3>
                              {basic?.video_file && basic?.video_status === 1 && (
                                <span className="advanced-card-badge video-badge">Video CV</span>
                              )}
                            </div>

                            {basic?.cv_headline && (
                              <p className="advanced-card-title">{basic.cv_headline}</p>
                            )}

                            <div className="advanced-card-meta">
                              {js.country_details?.name && (
                                <span>
                                  <i className="fa fa-map-marker"></i> Location:{" "}
                                  <strong>{js.country_details.name}</strong>
                                </span>
                              )}
                              {age && (
                                <span>
                                  Age: <strong>{age}</strong>
                                </span>
                              )}
                              {exp && (
                                <span>
                                  Work experience: <strong>{exp}</strong>
                                </span>
                              )}
                            </div>

                            {basic?.profile_score !== undefined && (
                              <div className="advanced-card-profile-score">
                                <div className="profile-score-bar">
                                  <div
                                    className="profile-score-fill"
                                    style={{ width: `${basic.profile_score}%` }}
                                  ></div>
                                </div>
                                <span className="profile-score-label">{basic.profile_score}% complete</span>
                              </div>
                            )}

                            {eduInfo && (
                              <div className="advanced-card-education">
                                <span className="advanced-card-edu-label">Education</span>
                                <span className="advanced-card-edu-value">
                                  {eduInfo.summary}
                                  {eduInfo.extra > 0 && (
                                    <span className="advanced-card-edu-more">
                                      {" "}+{eduInfo.extra} more education
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load More */}
                {pagination?.has_more && (
                  <div className="advanced-search-load-more">
                    <button
                      className="advanced-load-more-btn"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Loading...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-chevron-down"></i> Load More
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="advanced-search-empty-state">
                <div className="advanced-search-empty-icon">
                  <i className="fa fa-user-times"></i>
                </div>
                <h2>No candidates found</h2>
                <p>Try adjusting your filters or search term.</p>
              </div>
            )}
          </main>
        </div>
      </div>

      {isViewProfileOpen && selectedJobseeker && (
        <ViewProfileModal
          isOpen={isViewProfileOpen}
          onClose={() => setIsViewProfileOpen(false)}
          jobseekerId={selectedJobseeker.basicDetails?.jobseeker_id}
        />
      )}
    </div>
  );
};

export default CandidateAdvancedMain;
